/**
 * EventBus - коммуникация между Shell и микрофронтендами
 *
 * События:
 * - module:loaded, module:mounted, module:unmounted, module:error
 * - auth:login, auth:logout, auth:token-refresh
 * - theme:change, locale:change
 * - navigation:change
 */

import type { EventBus } from './types'

type EventHandler<T = unknown> = (payload: T) => void

/**
 * Реализация Event Bus
 */
export class TLHEventBus implements EventBus {
  private handlers = new Map<string, Set<EventHandler>>()
  private debugMode = false

  constructor(options: { debug?: boolean } = {}) {
    this.debugMode = options.debug ?? false
  }

  /**
   * Подписаться на событие
   */
  on<T = unknown>(event: string, handler: EventHandler<T>): void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set())
    }
    this.handlers.get(event)?.add(handler as EventHandler)

    if (this.debugMode) {
      console.log(`[EventBus] Subscribed to "${event}"`)
    }
  }

  /**
   * Отписаться от события
   */
  off<T = unknown>(event: string, handler: EventHandler<T>): void {
    const eventHandlers = this.handlers.get(event)
    if (eventHandlers) {
      eventHandlers.delete(handler as EventHandler)
      if (eventHandlers.size === 0) {
        this.handlers.delete(event)
      }
    }

    if (this.debugMode) {
      console.log(`[EventBus] Unsubscribed from "${event}"`)
    }
  }

  /**
   * Отправить событие
   */
  emit<T = unknown>(event: string, payload: T): void {
    if (this.debugMode) {
      console.log(`[EventBus] Emit "${event}"`, payload)
    }

    const eventHandlers = this.handlers.get(event)
    if (eventHandlers) {
      for (const handler of eventHandlers) {
        try {
          handler(payload)
        } catch (error) {
          console.error(`[EventBus] Error in handler for "${event}":`, error)
        }
      }
    }

    // Wildcard handlers
    const wildcardHandlers = this.handlers.get('*')
    if (wildcardHandlers) {
      for (const handler of wildcardHandlers) {
        try {
          handler({ event, payload })
        } catch (error) {
          console.error('[EventBus] Error in wildcard handler:', error)
        }
      }
    }
  }

  /**
   * Подписаться на событие один раз
   */
  once<T = unknown>(event: string, handler: EventHandler<T>): void {
    const wrapper: EventHandler<T> = (payload) => {
      this.off(event, wrapper)
      handler(payload)
    }
    this.on(event, wrapper)
  }

  /**
   * Удалить все обработчики события
   */
  removeAllListeners(event?: string): void {
    if (event) {
      this.handlers.delete(event)
    } else {
      this.handlers.clear()
    }
  }

  /**
   * Получить количество обработчиков события
   */
  listenerCount(event: string): number {
    return this.handlers.get(event)?.size ?? 0
  }

  /**
   * Получить список всех событий с обработчиками
   */
  eventNames(): string[] {
    return Array.from(this.handlers.keys())
  }

  /**
   * Включить/выключить debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled
  }
}

// ============================================================================
// Предопределённые типы событий
// ============================================================================

/**
 * Типы событий модулей
 */
export interface ModuleEvents {
  'module:loaded': { moduleId: string }
  'module:mounted': { moduleId: string }
  'module:unmounted': { moduleId: string }
  'module:error': { moduleId: string; error: unknown }
}

/**
 * Типы событий авторизации
 */
export interface AuthEvents {
  'auth:login': { userId: string }
  'auth:logout': Record<string, never>
  'auth:token-refresh': { token: string }
}

/**
 * Типы событий темы
 */
export interface ThemeEvents {
  'theme:change': { theme: 'light' | 'dark' }
  'locale:change': { locale: string }
}

/**
 * Типы событий навигации
 */
export interface NavigationEvents {
  'navigation:change': { path: string; moduleId?: string }
}

/**
 * Все события
 */
export type ShellEvents = ModuleEvents & AuthEvents & ThemeEvents & NavigationEvents

// ============================================================================
// Singleton instance
// ============================================================================

let busInstance: TLHEventBus | null = null

/**
 * Получить singleton экземпляр EventBus
 */
export function getEventBus(): TLHEventBus {
  if (!busInstance) {
    busInstance = new TLHEventBus({
      debug: import.meta.env.DEV,
    })
  }
  return busInstance
}

/**
 * Создать новый экземпляр EventBus (для тестирования)
 */
export function createEventBus(options?: { debug?: boolean }): TLHEventBus {
  return new TLHEventBus(options)
}

export default TLHEventBus
